import type { PackRegistryEntry } from "./packs";
import { makeDirection, type Direction } from "./db";
import type { LanguageId } from "./references";

/**
 * Keep the first choice deliberately small: the explicitly recommended pack
 * comes first, then short A1 packs, then the rest by size. The complete
 * catalog stays available behind progressive disclosure.
 */
export function pickOnboardingRecommendations(
  entries: PackRegistryEntry[],
  recommendedId: string | null,
  limit = 3,
): PackRegistryEntry[] {
  return [...entries]
    .sort((a, b) => {
      const recommendationDelta =
        Number(b.id === recommendedId) - Number(a.id === recommendedId);
      if (recommendationDelta !== 0) return recommendationDelta;

      const levelDelta = Number(b.level === "A1") - Number(a.level === "A1");
      if (levelDelta !== 0) return levelDelta;

      return a.conceptCount - b.conceptCount || a.name.localeCompare(b.name);
    })
    .slice(0, Math.max(0, limit));
}

export function selectStarterPack(
  entries: PackRegistryEntry[],
  primary: LanguageId,
  learning: LanguageId,
): PackRegistryEntry | null {
  return (
    [...entries]
      .filter(
        (entry) =>
          entry.conceptCount >= 3 &&
          entry.languages.includes(primary) &&
          entry.languages.includes(learning),
      )
      .sort(
        (a, b) =>
          Number(b.level === "A1") - Number(a.level === "A1") ||
          a.conceptCount - b.conceptCount ||
          a.id.localeCompare(b.id),
      )[0] ?? null
  );
}

export function firstRunDirection(
  primary: LanguageId,
  learning: LanguageId,
): Direction {
  return makeDirection(learning, primary);
}

const ONBOARDING_SESSION_KEY = "lexibit:onboarding-session";

export type OnboardingSession = {
  version: 1;
  phase: "installing" | "practice" | "success";
  primary: LanguageId;
  learning: LanguageId;
  packId: string;
  packFile: string;
  deckId: string;
  cardKeys: string[];
  answered: number;
  correct: number;
  suggestedName: string;
};

export function createOnboardingSession(input: {
  primary: LanguageId;
  learning: LanguageId;
  packId: string;
  packFile: string;
  deckId: string;
  suggestedName: string;
}): OnboardingSession {
  return {
    version: 1,
    phase: "installing",
    ...input,
    cardKeys: [],
    answered: 0,
    correct: 0,
  };
}

export function transitionOnboardingSession(
  session: OnboardingSession,
  event:
    | { type: "installed"; cardKeys: string[] }
    | { type: "graded"; cardKey: string; quality: number },
): OnboardingSession {
  if (event.type === "installed") {
    if (session.phase !== "installing") return session;
    if (event.cardKeys.length < 3) {
      throw new Error("starter pack needs at least three cards");
    }
    return {
      ...session,
      phase: "practice",
      cardKeys: event.cardKeys.slice(0, 3),
      answered: 0,
      correct: 0,
    };
  }

  if (session.phase !== "practice") {
    throw new Error("onboarding practice is not active");
  }
  if (session.cardKeys[session.answered] !== event.cardKey) {
    throw new Error("unexpected onboarding card");
  }
  const answered = session.answered + 1;
  return {
    ...session,
    phase: answered === session.cardKeys.length ? "success" : "practice",
    answered,
    correct: session.correct + (event.quality >= 3 ? 1 : 0),
  };
}

function isOnboardingSession(value: unknown): value is OnboardingSession {
  if (!value || typeof value !== "object") return false;
  const session = value as Partial<OnboardingSession>;
  return (
    session.version === 1 &&
    (session.phase === "installing" ||
      session.phase === "practice" ||
      session.phase === "success") &&
    typeof session.primary === "string" &&
    typeof session.learning === "string" &&
    typeof session.packId === "string" &&
    typeof session.packFile === "string" &&
    typeof session.deckId === "string" &&
    Array.isArray(session.cardKeys) &&
    session.cardKeys.every((key) => typeof key === "string") &&
    typeof session.answered === "number" &&
    typeof session.correct === "number" &&
    typeof session.suggestedName === "string"
  );
}

export function saveOnboardingSession(session: OnboardingSession): void {
  localStorage.setItem(ONBOARDING_SESSION_KEY, JSON.stringify(session));
}

export function readOnboardingSession(): OnboardingSession | null {
  const raw = localStorage.getItem(ONBOARDING_SESSION_KEY);
  if (!raw) return null;
  try {
    const parsed: unknown = JSON.parse(raw);
    return isOnboardingSession(parsed) ? parsed : null;
  } catch {
    return null;
  }
}

export function clearOnboardingSession(): void {
  localStorage.removeItem(ONBOARDING_SESSION_KEY);
}

/**
 * OnboardingGuard's route-access rule, extracted so it is unit-testable:
 * once onboarding is complete every guarded route opens normally. Before
 * that, only routes marked `allowInProgress` (currently /drill) stay
 * reachable, and only once the persisted pack session has moved past the
 * "installing" phase. The "Свои слова" path completes onboarding
 * immediately without ever persisting a session, so it never needs the
 * in-progress exception.
 */
export function canAccessGuardedRoute(
  onboardingCompleted: boolean,
  allowInProgress: boolean,
  session: OnboardingSession | null,
): boolean {
  if (onboardingCompleted) return true;
  return allowInProgress && session?.phase !== "installing";
}
